// Cliente HTTP tipado: el FRONT conoce el BACK por tipos.
// Toda respuesta usa el contrato de @shared/api. Si el backend cambia,
// TypeScript avisa en el frontend automáticamente.

import type { ApiError, ApiResponse } from "@shared/api";
import { clearToken, getToken } from "@client/api/token";

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseJson<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiResponse<T> | ApiError;
  if (!res.ok || !body.ok) {
    if (res.status === 401) clearToken();
    const err = body as ApiError;
    throw new ApiClientError(res.status, err.error.code, err.error.message, err.error.details);
  }
  return (body as ApiResponse<T>).data;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...init.headers },
  });
  return parseJson<T>(res);
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, { ...init, method: "GET" });
}

export async function apiPost<TResponse, TBody>(
  path: string,
  body: TBody,
  init?: RequestInit,
): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: "POST", body: JSON.stringify(body) });
}

export async function apiPatch<TResponse, TBody>(
  path: string,
  body: TBody,
  init?: RequestInit,
): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: "PATCH", body: JSON.stringify(body) });
}

export async function apiDelete<TResponse>(path: string, init?: RequestInit): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: "DELETE" });
}
